import React, { useEffect, useState, useMemo } from 'react';
import {
  Drawer, Form, Input, InputNumber, Row, Col, Button, Select,
  Tag, DatePicker, Typography, Switch, Space, Divider, Spin
} from 'antd';
import { useSelector } from 'react-redux';
import {
  PlusOutlined, DeleteOutlined,
  CalendarOutlined, FileTextOutlined, TagOutlined, CloseOutlined,
  IdcardOutlined, LoadingOutlined
} from '@ant-design/icons';
import { getTenantId } from '../../services/helper';

const { Text } = Typography;

export interface EnrollmentInput {
  studentId: string;
  categoryId: string;
  serviceId: string;
  tenantId: string;
  startDate?: string;
  endDate?: string;
  price: number;
  noOfClasses?: number;
  isSimulationRequired?: boolean;
  simulationClasses?: number;
  onRoadClasses?: number;
  status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  documents: Array<{
    label: string;
    status: 'PENDING' | 'UPLOADED';
    required: boolean;
  }>;
}

interface EnrollmentFormModalProps {
  open: boolean;
  title: string;
  onCancel: () => void;
  onFinish: (values: EnrollmentInput) => void;
  form: any;
  initialValues?: Partial<EnrollmentInput>;
  isLoading?: boolean;   // ← add
}

const EnrollmentFormModal: React.FC<EnrollmentFormModalProps> = ({
  open, title, onCancel, onFinish, form, initialValues, isLoading  // ← add
}) => {

  const documentFieldName = 'documents';
  const categories = useSelector((state: any) => state.getCategoryReducer.categorydata);
  const students = useSelector((state: any) => state.userListReducer.userList);
  const services = useSelector((state: any) => state.getServiceReducer.servicedata);

  const [documentList, setDocumentList] = useState<Array<{ label: string; status: string; required: boolean }>>([]);
  const [newDocLabel, setNewDocLabel] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const allServicesFlat = (services && services.services) || [];
  const currentServiceId = selectedServiceId || form.getFieldValue?.('serviceId') || '';
  const currentService = allServicesFlat.find((s: any) => s._id === currentServiceId);
  const currentSvcType = currentService?.serviceType || currentService?.type;
  const isTraining = currentService && currentSvcType === 'TRAINING';

  const categoryId = Form.useWatch('categoryId', form);
  const serviceIdUser = Form.useWatch('serviceId', form);



  const formatDLInput = (value: string) => {
    if (!value) return '';
    const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const letters = clean.slice(0, 2).replace(/[^A-Z]/g, '');
    const rest = clean.slice(letters.length).replace(/[^0-9]/g, '').slice(0, 13);

    if (!letters) return '';
    if (rest.length <= 2) {
      return `${letters}${rest}`;
    }
    return `${letters}${rest.slice(0, 2)} ${rest.slice(2)}`;
  };

  const DL_REGEX = /^[A-Z]{2}\d{2}[\s-]?\d{11}$/;
  const dlValidationRule = {
    validator: (_: any, value: string) => {
      if (!value) return Promise.resolve();
      const normalized = value.trim().toUpperCase();
      if (!DL_REGEX.test(normalized)) {
        return Promise.reject(
          new Error('Format: TN01 XXXXXXX4039')
        );
      }
      return Promise.resolve();
    },
  };

  // RC format: State(2 letters) + RTO(2 digits) + Series(0-3 letters) + Number(4 digits) OR Bharat Series (e.g. 22BH1234AA)
  const RC_REGEX = /^([A-Z]{2}\d{2}[A-Z]{0,3}\d{4}|\d{2}BH\d{4}[A-Z]{1,2})$/;

  const formatRCInput = (value: string) => {
    if (!value) return '';
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  };

  const integerValidationRule = {
    validator: (_: any, value: any) => {
      if (value === undefined || value === null || value === '') return Promise.resolve();
      if (!Number.isInteger(Number(value))) {
        return Promise.reject(new Error('Please enter a valid whole number'));
      }
      return Promise.resolve();
    },
  };

  const blockDecimalKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const parseIntegerInput = (val: any) => {
    const cleaned = String(val ?? '').replace(/[^\d]/g, '');
    return cleaned === '' ? (null as any) : Number(cleaned);
  };

  const rcValidationRule = {
    validator: (_: any, value: string) => {
      if (!value) return Promise.resolve();
      const clean = value.replace(/[\s-]/g, '').toUpperCase();
      if (!RC_REGEX.test(clean)) {
        return Promise.reject(
          new Error('Invalid RC number. Format: TN01AB1234')
        );
      }
      return Promise.resolve();
    },
  };

  const categoryOptions = useMemo(() => {
    const payload = categories?.categories;
    const list = payload?.categories ?? payload ?? [];
    const filtered = (Array.isArray(list) ? list : []).filter((c: any) => c.categoryType !== 'QUERY');
    return filtered;
  }, [categories]);



  useEffect(() => {
  const serviceId = selectedServiceId || serviceIdUser;

  if (!serviceId || !services?.services) {
    return;
  }
  const selectedService = services.services.find((s: any) => s._id === serviceId);
  if (!selectedService) {
    return;
  }
  const currentPrice = form.getFieldValue('price');
  if (selectedService.price != null && !currentPrice) {
    form.setFieldsValue({
      price: selectedService.price,
    });
  }
  const currentDocs = form.getFieldValue(documentFieldName);
  const hasExistingDocs = Array.isArray(currentDocs) && currentDocs.length > 0;

  if (!hasExistingDocs && Array.isArray(selectedService.requiredDocuments)) {
    const docs = selectedService.requiredDocuments.map(
      (docLabel: string) => ({
        label: docLabel,
        status: 'PENDING',
        required: false,
      })
    );

    setDocumentList(docs);
    form.setFieldsValue({[documentFieldName]: docs, });
  } else if (!hasExistingDocs) {
    setDocumentList([]);
    form.setFieldsValue({
      [documentFieldName]: [],
    });
  }

}, [selectedServiceId, serviceIdUser, services, form]);



  useEffect(() => {
    console.log('EnrollmentFormModal useEffect: form values changed:',serviceIdUser);

  }, [serviceIdUser]);

  useEffect(() => {
    try {
      const catId = form.getFieldValue('categoryId');
      setSelectedCategoryId(catId || '');
    } catch (e) { }
  }, [form]);

  const handleAddDocument = () => {
    const label = newDocLabel.trim();
    if (!label) return;
    const exists = documentList.some((doc) => doc.label === label);
    if (!exists) {
      const nextDocs = [...documentList, { label, status: 'PENDING', required: false }];
      setDocumentList(nextDocs);
      form.setFieldsValue({ [documentFieldName]: nextDocs });
    }
    setNewDocLabel('');
  };

  const handleDocumentStatusChange = (index: number, newStatus: string) => {
    const nextDocs = documentList.map((doc, i) =>
      i === index ? { ...doc, status: newStatus } : doc
    );
    setDocumentList(nextDocs);
    form.setFieldsValue({ [documentFieldName]: nextDocs });
  };

  const handleDocumentRequiredChange = (index: number, isRequired: boolean) => {
    const nextDocs = documentList.map((doc, i) =>
      i === index ? { ...doc, required: isRequired } : doc
    );
    setDocumentList(nextDocs);
    form.setFieldsValue({ [documentFieldName]: nextDocs });
  };

  const handleRemoveDocument = (index: number) => {
    const nextDocs = documentList.filter((_, i) => i !== index);
    setDocumentList(nextDocs);
    form.setFieldsValue({ [documentFieldName]: nextDocs });
  };

  const handleFinish = (values: any) => {
    const allServices = (services && services.services) || [];
    const selectedService = allServices.find((s: any) => s._id === values.serviceId);

    const payload: any = {
      studentId: values.studentId,
      categoryId: values.categoryId,
      serviceId: values.serviceId,
      drivingLicenseNo: values.drivingLicenseNo,
      dlIssuedDate: values.dlIssuedDate?.format('YYYY-MM-DD'),
      rcNumber: values.rcNumber,
      price: values.price,
      status: values.status || 'ASSIGNED',
      documents: documentList.map((doc) => ({
        label: doc.label,
        status: doc.status as 'PENDING' | 'UPLOADED',
        required: doc.required ?? false,
      })),
    };

    const svcType = selectedService?.serviceType || selectedService?.type;
    if (selectedService && svcType === 'TRAINING') {
      payload.noOfClasses = values.noOfClasses;
      payload.isSimulationRequired = values.isSimulationRequired || false;
      if (values.isSimulationRequired) {
        payload.simulationClasses = values.simulationClasses;
        payload.onRoadClasses = values.onRoadClasses;
        const total = (values.simulationClasses || 0) + (values.onRoadClasses || 0);
        if (total !== values.noOfClasses) {
          form.setFields([
            {
              name: 'simulationClasses',
              errors: [
                `Simulation (${values.simulationClasses || 0}) + On-Road (${values.onRoadClasses || 0}) must equal No. of Classes (${values.noOfClasses})`,
              ],
            },
          ]);
          return;
        }
      }
    }
    onFinish(payload as EnrollmentInput);
  };

  // ── Fix: on 

  useEffect(() => {
    if (!open) return;
    const currentCat = form.getFieldValue('categoryId') || initialValues?.categoryId;
    const currentSvc = form.getFieldValue('serviceId') || initialValues?.serviceId;
    if (currentCat && currentCat !== selectedCategoryId) {
      console.log('Setting selectedCategoryId from form value:', currentCat);
      setSelectedCategoryId(currentCat);
    }
    if (currentSvc && currentSvc !== selectedServiceId) {
      console.log('Setting selectedServiceId from form value:', currentSvc);
      setSelectedServiceId(currentSvc);
    }
  }, [form]);


  useEffect(() => {
    if (open) {
      form.resetFields();
      setDocumentList([]);
      setSelectedServiceId('');
      setNewDocLabel('');

      if (initialValues) {
        form.setFieldsValue(initialValues);
        if ((initialValues as any)?.categoryId) {
          setSelectedCategoryId((initialValues as any).categoryId);
        }
        if ((initialValues as any)?.serviceId) {
          setSelectedServiceId((initialValues as any).serviceId);
        }
      } else {
        form.setFieldsValue({ status: 'ASSIGNED' });
      }
    }
  }, [open, initialValues, form]);



  return (
    <Drawer
      className="enf-drawer"
      open={open}
      onClose={onCancel}
      width={window.innerWidth > 1400 ? 530 : 480}
      closable={false}
      destroyOnClose
      title={null}
      footer={
        <div className="enf-drawer-footer">
          <Button className="enf-btn-cancel" onClick={onCancel} disabled={!!isLoading}>
            Cancel
          </Button>
          <Button
            className="enf-btn-save"
            type="primary"
            onClick={() => form.submit()}
            loading={!!isLoading}
            disabled={!!isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Enrollment'}
          </Button>
        </div>
      }
    >
      {isLoading && (
        <div className="svc-spinner-overlay">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 40, color: 'var(--primary-color)' }} spin />}
          />
          <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14 }}>
            Saving enrollment...
          </span>
        </div>
      )}

      {/* ── Sticky Header ── */}
      <div className="enf-drawer-header">
        <div className="enf-drawer-header-inner">
          <div className="enf-drawer-title-group">
            <div className="enf-drawer-icon-box">
              <IdcardOutlined />
            </div>
            <div className="enf-drawer-title-text">
              <span className="enf-drawer-title">{title}</span>
              <span className="enf-drawer-subtitle">Fill in the details to create an enrollment</span>
            </div>
          </div>
          <button className="enf-drawer-close-btn" onClick={onCancel} aria-label="Close">
            <CloseOutlined />
          </button>
        </div>
        <Divider className="enf-drawer-header-divider" />
      </div>

      <Form form={form} layout="vertical" onFinish={handleFinish} className="enf-form">

        {/* ══ Section: Student & Classification ══ */}
        <div className="enf-form-section">

          <Form.Item
            name="studentId"
            label="Student"
            rules={[{ required: true, message: 'Please select a student' }]}
            className="enf-form-item"
          >
            <Select
              className="enf-select"
              placeholder="Search and select a student"
              showSearch
              listHeight={200}
              filterOption={(input, option: any) => {
                const label = option?.label
                  ?? (Array.isArray(option?.children)
                    ? option.children.join(' ')
                    : String(option?.children ?? ''));
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {students?.users?.map((student: any) => (
                <Select.Option
                  key={student._id}
                  value={student._id}
                  label={`${student.firstName} ${student.lastName}`}
                >
                  {student.firstName} {student.lastName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="categoryId"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
                className="enf-form-item"
              >
                <Select
                  className="enf-select"
                  placeholder="Select category"
                  listHeight={200}
                  onChange={(value) => {
                    form.setFieldsValue({ serviceId: undefined });
                    setSelectedServiceId('');
                    setSelectedCategoryId(value);
                  }}
                >
                  {categoryOptions.map((category: any) => (
                    <Select.Option key={category._id} value={category._id}>
                      {category.categoryName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="serviceId"
                label="Service" rules={[{ required: true, message: 'Please select a service' }]}
                className="enf-form-item"
              >
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) =>
                    prevValues.categoryId !== curValues.categoryId
                  }
                >
                  {() => {
                    const categoryId = form.getFieldValue('categoryId') || selectedCategoryId;
                    const filteredServices = categoryId
                      ? allServicesFlat.filter((s: any) => {
                        const serviceCategoryId =
                          s?.category?._id || s?.categoryId || s?.category || s?.categoryId?._id;
                        return String(serviceCategoryId) === String(categoryId);
                      })
                      : [];
                    return (
                      <Select
                        className="enf-select"
                        placeholder={categoryId ? 'Select service' : 'Select category first'}
                        showSearch
                        listHeight={200}
                        disabled={!categoryId}
                        value={form.getFieldValue('serviceId') || selectedServiceId || undefined}
                        filterOption={(input, option: any) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value: any) => {
                          setSelectedServiceId(value);
                          form.setFieldsValue({ serviceId: value });
                          const sel = allServicesFlat.find((s: any) => s._id === value);
                          if (sel?.price) {
                            form.setFieldsValue({ price: sel.price });
                          }
                          const sType = sel?.serviceType || sel?.type;
                          if (sel && sType !== 'TRAINING') {
                            form.setFieldsValue({ noOfClasses: undefined });
                          }
                        }}
                      >
                        {filteredServices.map((svc: any) => (
                          <Select.Option key={svc._id} value={svc._id}>
                            {svc.serviceName}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          <Divider className="enf-section-divider" />
        </div>

        <div className="enf-form-section">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select status' }]}
                className="enf-form-item" >
                <Select className="enf-select" placeholder="Select status">
                  <Select.Option value="ASSIGNED">Assigned</Select.Option>
                  <Select.Option value="IN_PROGRESS">In Progress</Select.Option>
                  <Select.Option value="COMPLETED">Completed</Select.Option>
                  <Select.Option value="CANCELLED">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rcNumber" label={<span className="enf-label">RC No.</span>} normalize={formatRCInput}
                rules={[rcValidationRule]} className="enf-form-item" >
                <Input className="enf-input" placeholder="e.g. TN01AB1234" maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
          <Divider className="enf-section-divider" />
        </div>
        <div className="enf-form-section">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="price" label="Price (₹)" rules={[{ required: true, message: 'Please enter price' }]} className="enf-form-item">
                <InputNumber className="enf-input-number" disabled
                  placeholder="Auto-filled from service" min={0} prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="discount" label={<span className="enf-label">Discount (₹)</span>} className="enf-form-item">
                <InputNumber className="enf-input-number" placeholder="e.g. 500" min={0} prefix="₹"
                  onChange={(value: number | null) => {
                    const discount = Number(value || 0);
                    const selectedService = services?.services?.find((s: any) => s._id === selectedServiceId);
                    const basePrice =
                      selectedService && typeof selectedService.price === 'number'
                        ? selectedService.price
                        : (form.getFieldValue('price') ?? 0);
                    const newPrice = Math.max(basePrice - discount, 0);
                    form.setFieldsValue({ price: newPrice });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
        {isTraining && (
          <>
            <div className="enf-form-section">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="noOfClasses" label="No. of Classes"
                    rules={[
                      { required: true, message: 'Please enter number of classes' },
                      integerValidationRule,
                    ]}
                    className="enf-form-item"
                  >
                    <InputNumber className="enf-input-number"
                      min={1} precision={0} step={1} parser={parseIntegerInput}
                      onKeyDown={blockDecimalKeys} placeholder="e.g. 20" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="isSimulationRequired"
                    label="Simulation Required?" valuePropName="checked"
                    className="enf-form-item">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" className="enf-switch" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.isSimulationRequired !== cur.isSimulationRequired ||
                  prev.noOfClasses !== cur.noOfClasses
                }
              >
                {() => {
                  const isSimReq = form.getFieldValue('isSimulationRequired');
                  if (!isSimReq) return null;
                  return (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="simulationClasses"
                          label="Simulation Classes"
                          rules={[
                            { required: true, message: 'Required' },
                            integerValidationRule,
                            {
                              validator: (_: any, value: number) => {
                                const total = form.getFieldValue('noOfClasses') || 0;
                                if (value > total)
                                  return Promise.reject(`Cannot exceed ${total}`);
                                const onRoad = form.getFieldValue('onRoadClasses') || 0;
                                if (value + onRoad > total)
                                  return Promise.reject(`Sum exceeds ${total}`);
                                return Promise.resolve();
                              },
                            },
                          ]}
                          className="enf-form-item"
                        >
                          <InputNumber
                            className="enf-input-number"
                            min={0}
                            precision={0}
                            step={1}
                            parser={parseIntegerInput}
                            onKeyDown={blockDecimalKeys}
                            max={form.getFieldValue('noOfClasses') || 0}
                            placeholder="e.g. 3"
                            onChange={(val: number | null) => {
                              const total = form.getFieldValue('noOfClasses') || 0;
                              const sim = Math.floor(val || 0);
                              form.setFieldsValue({
                                onRoadClasses: Math.max(total - sim, 0),
                              });
                              form.validateFields(['simulationClasses', 'onRoadClasses']);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="onRoadClasses"
                          label="On-Road Classes"
                          rules={[
                            { required: true, message: 'Required' },
                            integerValidationRule,
                            {
                              validator: (_: any, value: number) => {
                                const total = form.getFieldValue('noOfClasses') || 0;
                                if (value > total)
                                  return Promise.reject(`Cannot exceed ${total}`);
                                const sim = form.getFieldValue('simulationClasses') || 0;
                                if (value + sim > total)
                                  return Promise.reject(`Sum exceeds ${total}`);
                                return Promise.resolve();
                              },
                            },
                          ]}
                          className="enf-form-item"
                        >
                          <InputNumber
                            className="enf-input-number"
                            min={0}
                            precision={0}
                            step={1}
                            parser={parseIntegerInput}
                            onKeyDown={blockDecimalKeys}
                            max={form.getFieldValue('noOfClasses') || 0}
                            placeholder="e.g. 2"
                            onChange={(val: number | null) => {
                              const total = form.getFieldValue('noOfClasses') || 0;
                              const onRoad = Math.floor(val || 0);
                              form.setFieldsValue({
                                simulationClasses: Math.max(total - onRoad, 0),
                              });
                              form.validateFields(['simulationClasses', 'onRoadClasses']);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>
              {/* <Divider className="enf-section-divider" /> */}
            </div>

          </>
        )}

        {/* ══ Section: License Details (conditional) ══ */}
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.status !== cur.status}>
          {({ getFieldValue }) => {
            const status = getFieldValue('status');
            return (
              <>
                <div className="enf-form-section">
                  {status === 'COMPLETED' ? (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="drivingLicenseNo"
                          label="DL Number"
                          normalize={formatDLInput}
                          rules={[
                            { required: true, message: 'DL number required for completed enrollment' },
                            dlValidationRule,
                          ]}
                          className="enf-form-item"
                        >
                          <Input
                            className="enf-input"
                            placeholder="e.g.TN01 XXXXXXX4039"
                            maxLength={16}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="dlIssuedDate"
                          label="DL Issued Date"
                          rules={[
                            { required: true, message: 'DL issued date required for completed enrollment' },
                          ]}
                          className="enf-form-item"
                        >
                          <DatePicker
                            className="enf-datepicker"
                            placeholder="Select DL issued date"
                            suffixIcon={<CalendarOutlined className="enf-datepicker-icon" />}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  ) : (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="drivingLicenseNo"
                          label={<span className="enf-label">DL Number</span>}
                          normalize={formatDLInput}
                          rules={[dlValidationRule]}
                          className="enf-form-item"
                        >
                          <Input
                            className="enf-input"
                            placeholder="e.g. TN01 XXXXXXX4039"
                            maxLength={16}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                  <Divider className="enf-section-divider" />
                </div>

              </>
            );
          }}
        </Form.Item>

        {/* ══ Section: Required Documents ══ */}
        <div className="enf-form-section enf-form-section--last">
          <div className="enf-doc-add-header">
            <span className="enf-doc-add-label">Document</span>
          </div>
          {/* Add document input */}
          <div className="enf-doc-add-row">
            <Input
              className="enf-input enf-doc-input"
              placeholder="Add document name (e.g. AADHAR, PAN CARD)"
              value={newDocLabel}
              onChange={(e) => setNewDocLabel(e.target.value)}
              onPressEnter={handleAddDocument}
            />
            <Button
              className="enf-doc-add-btn"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddDocument}
            >
              Add
            </Button>
          </div>

          {/* Document list */}
          {documentList.length > 0 ? (
            <div className="enf-doc-list">
              {/* Header row */}
              <div className="enf-doc-list-head">
                <span className="enf-doc-col-label">Document</span>
                <span className="enf-doc-col-req">Required</span>
                <span className="enf-doc-col-status">Status</span>
                <span className="enf-doc-col-action" />
              </div>
              {documentList.map((doc, index) => (
                <div key={index} className="enf-doc-row">
                  <span className="enf-doc-name">{doc.label}</span>
                  <div className="enf-doc-req-toggle">
                    <Switch
                      size="small"
                      checked={doc.required}
                      onChange={(checked) => handleDocumentRequiredChange(index, checked)}
                      className="enf-switch"
                    />
                    <Text className={`enf-doc-req-text${doc.required ? ' enf-doc-req-text--on' : ''}`}>
                      {doc.required ? 'Required' : 'Optional'}
                    </Text>
                  </div>
                  <Select
                    className="enf-doc-status-select"
                    value={doc.status}
                    onChange={(value) => handleDocumentStatusChange(index, value)}
                  >
                    <Select.Option value="PENDING">
                      <Tag color="orange" className="enf-status-tag">Pending</Tag>
                    </Select.Option>
                    <Select.Option value="UPLOADED">
                      <Tag color="green" className="enf-status-tag">Uploaded</Tag>
                    </Select.Option>
                  </Select>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveDocument(index)}
                    className="enf-doc-remove-btn"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="enf-doc-empty">
              <FileTextOutlined className="enf-doc-empty-icon" />
              <Text className="enf-doc-empty-text">
                {selectedServiceId
                  ? 'No required documents for this service. You can add documents above.'
                  : 'Select a service first — documents will be auto-loaded.'}
              </Text>
            </div>
          )}
        </div>

      </Form>
    </Drawer>
  );
};

/* tiny inline-safe icon shim to avoid a missing antd icon */
const IdCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <TagOutlined className={className} />
);

export default EnrollmentFormModal;